export function uniqueKeywordList(values = [], normalizeText) {
  const seen = new Set();
  const result = [];
  for (const value of values) {
    const keyword = normalizeText(value);
    const key = keyword.toLowerCase();
    if (!keyword || seen.has(key)) continue;
    seen.add(key);
    result.push(keyword);
  }
  return result;
}

export function buildSearchTermsOutputNotes({ selectedKeywords, filteredSelected, packed, normalizeText }) {
  const keyFor = (value) => normalizeText(value).toLowerCase();
  const filteredKeys = new Set(filteredSelected.map(keyFor));
  const packedKeys = new Set(packed.map(keyFor));
  const policyFiltered = selectedKeywords.filter((keyword) => !filteredKeys.has(keyFor(keyword)));
  const budgetFiltered = filteredSelected.filter((keyword) => !packedKeys.has(keyFor(keyword)));
  const preview = (keywords) => `${keywords.slice(0, 8).join(', ')}${keywords.length > 8 ? ` 等 ${keywords.length} 个` : ''}`;
  const notes = [];
  if (policyFiltered.length) {
    notes.push({
      title: `${policyFiltered.length} 个已选词未写入输出`,
      body: `这些词命中准入策略，不进入后台 Search Terms（可能属于推荐采纳、广告测试、禁用，或被标记为不写后台词）：${preview(policyFiltered)}。`,
    });
  }
  if (budgetFiltered.length) {
    notes.push({
      title: `${budgetFiltered.length} 个已选词被 250 bytes 上限截断`,
      body: `这些词已通过准入策略，但打包时超出 Search Terms 字节上限，暂未进入右侧输出：${preview(budgetFiltered)}。`,
    });
  }
  return notes;
}

export function createSearchTermsOutputController({
  select,
  getState,
  setState,
  setStateBulk,
  normalizeText,
  filterSelectedKeywords,
  packKeywords,
  maxBytes,
  normalizeDraft,
  updateByteCounter,
  renderSummary,
  createElement,
  createTextNode,
  getSelectionMode,
  clipboard = navigator.clipboard,
}) {
  function createOutputNotes(notes = []) {
    return notes.map((note) => {
      const item = createElement('div', 'st-output-filter-note');
      item.append(createElement('strong', '', note.title), document.createElement('br'), createTextNode(note.body));
      return item;
    });
  }

  function renderTranslationStatus(fallbackLoadingText = '') {
    const status = select('#searchterms-translation-status');
    if (!status) return;
    const loading = !!getState('searchTermsTranslationLoading');
    const error = normalizeText(getState('searchTermsTranslationError') || '');
    const warning = normalizeText(getState('searchTermsTranslationWarning') || '');
    status.classList.toggle('is-error', !!error && !loading);
    status.classList.toggle('is-warning', !error && !!warning && !loading);
    if (loading) {
      status.textContent = fallbackLoadingText || '翻译中...';
      status.hidden = false;
    } else if (error) {
      status.textContent = `翻译失败：${error}`;
      status.hidden = false;
    } else if (warning) {
      status.textContent = warning;
      status.hidden = false;
    } else {
      status.textContent = '';
      status.hidden = true;
    }
  }

  function update() {
    const selected = getState('selectedStKeywords') || new Set();
    const keywordUsePolicies = getState('keywordUsePolicies') || [];
    const selectedKeywords = uniqueKeywordList([...selected], normalizeText);
    const filteredSelected = filterSelectedKeywords(selectedKeywords, keywordUsePolicies);
    const { packed, bytes } = packKeywords(filteredSelected, maxBytes);
    const outputText = packed.join(', ');
    const outputNotes = buildSearchTermsOutputNotes({ selectedKeywords, filteredSelected, packed, normalizeText });
    renderSummary(select('#keyword-summary-view'), {
      keywordLibrary: getState('keywordLibrary'),
      selectedStKeywords: selected,
      byteCount: bytes,
      conflictResult: getState('keywordConflictResult'),
      keywordUsePolicies,
      businessContext: getState('businessContext'),
      searchTermsText: outputText,
      selectionMode: getSelectionMode(),
    });
    const output = select('#st-output-text');
    const counter = select('#st-byte-counter');
    const translateButton = select('#translate-searchterms');
    const showingZh = !!getState('searchTermsShowingZh');
    if (output) {
      const translations = getState('stCandidatesTranslationMap') || {};
      const translated = packed.map((keyword) => translations[keyword.toLowerCase()] || keyword);
      const displayKeywords = showingZh && translated.length ? translated : packed;
      const emptyText = selectedKeywords.length
        ? '已选关键词当前没有可写入 Search Terms 的输出。'
        : '从左侧勾选关键词，生成 Search Terms...';
      const outputNode = displayKeywords.length
        ? (() => {
          const grid = createElement('div', 'st-output-pill-grid');
          grid.append(...displayKeywords.map((item) => createElement('span', 'st-output-pill', String(item).trim())));
          return grid;
        })()
        : createElement('div', 'st-output-placeholder', emptyText);
      output.replaceChildren(outputNode, ...createOutputNotes(outputNotes));
      output.classList.toggle('translated-view', showingZh && translated.length > 0);
    }
    const meta = select('#st-output-meta');
    if (meta) {
      const notOutputCount = selectedKeywords.length - packed.length;
      meta.textContent = outputText
        ? `${packed.length} 词 · ${bytes} bytes${notOutputCount > 0 ? ` · ${notOutputCount} 未输出` : ''}`
        : (selectedKeywords.length ? `${selectedKeywords.length} 已选 · 0 词输出` : '等待选择关键词');
    }
    updateByteCounter(counter, bytes, maxBytes, { format: 'en', showOkAtZero: false });
    if (translateButton) {
      const candidates = getState('stCandidatesSourceKeywords') || [];
      translateButton.disabled = !candidates.length || getState('searchTermsTranslationLoading');
      translateButton.textContent = showingZh ? '查看原文' : '中文理解';
    }
    renderTranslationStatus();
    setStateBulk({
      step02SearchTermsSelectionDraft: normalizeDraft({
        searchTermsEn: outputText,
        candidates: packed,
        selected: packed,
        approvedKeywords: selectedKeywords,
        error: null,
      }),
    });
  }

  function fillToBudget(targetBytes = 240) {
    const selected = new Set(getState('selectedStKeywords') || []);
    const keywordUsePolicies = getState('keywordUsePolicies') || [];
    const candidates = Array.isArray(getState('stCandidatesSourceKeywords')) ? getState('stCandidatesSourceKeywords') : [];
    const target = Math.max(1, Number(targetBytes || 240));
    for (const item of candidates) {
      const keyword = String(item?.keyword || '').trim();
      if (!keyword) continue;
      selected.add(keyword);
      const filtered = filterSelectedKeywords(
        [...selected],
        keywordUsePolicies,
        { operatorApproved: false },
      );
      if (packKeywords(filtered, maxBytes).bytes > target) selected.delete(keyword);
    }
    setState('selectedStKeywords', selected);
  }

  function copyCurrent() {
    const output = getState('step02SearchTermsSelectionDraft')?.searchTermsEn || '';
    if (output) clipboard?.writeText(output).catch(() => {});
  }

  function handleCheckboxChange(event, { getSelectionMode }) {
    const checkbox = event.target;
    const keyword = checkbox.dataset.keyword;
    if (!keyword) return;
    const selected = new Set(getState('selectedStKeywords') || []);
    if (checkbox.checked) selected.add(keyword);
    else selected.delete(keyword);
    const tag = typeof checkbox.closest === 'function' ? checkbox.closest('.st-candidate-tag') : null;
    tag?.classList.toggle('selected', checkbox.checked);
    if (getSelectionMode() === 'ai') setState('stSelectionMode', 'manual');
    setState('selectedStKeywords', selected);
    setState('keywordConflictResult', null);
    setState('lastDetectedConflicts', []);
  }

  return { copyCurrent, fillToBudget, handleCheckboxChange, renderTranslationStatus, update };
}
